import React, { useState } from 'react'
import { IoClose, IoCheckmarkCircle, IoArrowBack } from 'react-icons/io5'
import { toast } from 'react-toastify'
import { Link, useLocation } from 'react-router-dom'
import { useTimer } from '../../../hooks/useTimer'
import { useTimerData } from '../../../providers/TimerDataProvider'
import { planningApi } from '../../../pages/Planning/services/api'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/modules/rootReducer'
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  ModalTitle,
  ModalSubtitle,
  ButtonsContainer,
  ActionButton,
} from './styles'

interface IBlockTimerModalProps {
  isOpen: boolean
  onClose: () => void
  onContinue?: () => void
  onFinish?: () => void
}

/**
 * Modal exibido quando usuário tenta mudar plano durante timer ativo
 * Oferece duas opções:
 * 1. Continuar estudando (bloqueia ação e fecha modal)
 * 2. Finalizar sessão agora (finaliza sessão e permite ação)
 */
const BlockTimerModal: React.FC<IBlockTimerModalProps> = ({
  isOpen,
  onClose,
  onContinue,
  onFinish,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { currentSession, resetTimer, timeRemaining } = useTimer()
  const { refetch } = useTimerData()
  const currentPlanId = useSelector((state: RootState) => state.studyPlan.currentPlanId)
  const location = useLocation()

  if (!isOpen || !currentSession) return null

  // Handler para finalizar sessão
  const handleFinishSession = async () => {
    if (!currentPlanId || !currentSession) return

    setIsLoading(true)

    try {
      // Marca sessão como 'done' na API
      await planningApi.patchCycleSession(currentPlanId, currentSession.id, 'done')

      // Refetch para pegar a próxima sessão
      refetch()

      // Se estiver na página de planejamento, dispara evento para atualizar o ciclo
      if (location.pathname === '/planejamento') {
        window.dispatchEvent(
          new CustomEvent('cycleSessionCompleted', { detail: { planId: currentPlanId } })
        )
      }

      // Toast com link para planejamento
      toast.success(
        ({ closeToast }) => (
          <div>
            <div style={{ marginBottom: '8px' }}>
              Sessão de {currentSession.subject} finalizada!
            </div>
            <Link
              to="/planejamento"
              onClick={closeToast}
              style={{
                color: '#fff',
                textDecoration: 'underline',
                fontWeight: 500,
                display: 'inline-block',
                marginTop: '4px',
              }}
            >
              Ir para Planejador
            </Link>
          </div>
        ),
        {
          position: 'top-right',
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      )

      // Reseta o timer
      resetTimer()

      // Fecha modal
      onClose()

      // Chama callback de finalização se fornecido
      if (onFinish) {
        onFinish()
      }
    } catch (error: any) {
      console.error('Erro ao finalizar sessão:', error)
      toast.error(error?.response?.data?.message || 'Erro ao finalizar sessão. Tente novamente.', {
        position: 'top-right',
        autoClose: 4000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Handler para continuar estudando
  const handleContinue = () => {
    onClose()
    if (onContinue) {
      onContinue()
    }
  }

  // Handler para ESC key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isLoading) {
      handleContinue()
    }
  }

  return (
    <ModalOverlay onClick={handleContinue} onKeyDown={handleKeyDown}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleContinue} aria-label="Fechar">
          <IoClose />
        </CloseButton>

        <ModalTitle>Conclua sua sessão de estudos primeiro</ModalTitle>

        <ModalSubtitle>
          Você tem uma sessão de <strong>{currentSession.subject}</strong> em andamento. Para evitar
          inconsistências, finalize a sessão antes de modificar o plano de estudos.
        </ModalSubtitle>

        <ButtonsContainer>
          <ActionButton variant="secondary" onClick={handleContinue} disabled={isLoading}>
            <IoArrowBack />
            Continuar
          </ActionButton>
          <ActionButton variant="primary" onClick={handleFinishSession} disabled={isLoading}>
            <IoCheckmarkCircle />
            {isLoading ? 'Finalizando...' : 'Finalizar sessão'}
          </ActionButton>
        </ButtonsContainer>
      </ModalContainer>
    </ModalOverlay>
  )
}

export default BlockTimerModal
